'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';

const RANGES = ['1D', '5D', '1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', '10Y'] as const;
const RANGE_MAP: Record<string, string> = {
  '1D': '1d', '5D': '5d', '1M': '1mo', '3M': '3mo',
  '6M': '6mo', '1Y': '1y', '2Y': '2y', '3Y': '3y',
  '5Y': '5y', '10Y': '10y',
};

const SPX_COLOR = '#3B82F6';
const VIX_COLOR = '#F59E0B';

interface SpxVixChartProps {
  height?: number;
  defaultRange?: string;
}

type ChartPoint = { timestamp: number; close: number };

async function fetchChart(symbol: string, range: string): Promise<ChartPoint[]> {
  const res = await fetch(`/api/terminal/chart/${encodeURIComponent(symbol)}?range=${range}`);
  if (!res.ok) throw new Error('Failed to fetch chart');
  return res.json();
}

export function SpxVixChart({ height = 300, defaultRange = '1M' }: SpxVixChartProps) {
  const [range, setRange] = useState(defaultRange);
  const apiRange = RANGE_MAP[range] || '1mo';

  const { data: spxData, isLoading: spxLoading } = useQuery<ChartPoint[]>({
    queryKey: ['/api/terminal/chart', 'SPY', apiRange],
    queryFn: () => fetchChart('SPY', apiRange),
    refetchInterval: range === '1D' ? 30000 : 300000,
  });

  const { data: vixData, isLoading: vixLoading } = useQuery<ChartPoint[]>({
    queryKey: ['/api/terminal/chart', '^VIX', apiRange],
    queryFn: () => fetchChart('^VIX', apiRange),
    refetchInterval: range === '1D' ? 30000 : 300000,
  });

  const isLoading = spxLoading || vixLoading;

  const spxByTs = new Map<number, number>();
  (spxData || []).forEach((d) => spxByTs.set(d.timestamp, d.close));
  const vixByTs = new Map<number, number>();
  (vixData || []).forEach((d) => vixByTs.set(d.timestamp, d.close));

  const intraday = range === '1D' || range === '5D';
  const allTimestamps = Array.from(new Set([
    ...(spxData || []).map((d) => d.timestamp),
    ...(vixData || []).map((d) => d.timestamp),
  ])).sort((a, b) => a - b);

  const merged = allTimestamps.map((ts) => ({
    timestamp: ts,
    spx: spxByTs.get(ts) ?? null,
    vix: vixByTs.get(ts) ?? null,
    time: new Date(ts * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(intraday ? { hour: 'numeric', minute: '2-digit' } : {}),
    }),
  }));

  const hasData = merged.length > 0;

  return (
    <div>
      <div className="flex items-center gap-1 mb-3">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={`px-2 py-0.5 text-xs font-mono transition-colors ${
              range === r
                ? 'bg-[var(--color-accent)] text-white'
                : 'text-[#6B7280] hover:text-[#E5E7EB] hover:bg-[#1F2937]'
            }`}
          >
            {r}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3 text-[10px] font-mono">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-0.5" style={{ backgroundColor: SPX_COLOR }} />
            <span className="text-[#9CA3AF]">S&P 500 (SPY)</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-0.5" style={{ backgroundColor: VIX_COLOR }} />
            <span className="text-[#9CA3AF]">VIX</span>
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="w-full bg-[#1F2937] animate-pulse rounded" style={{ height }} />
      ) : !hasData ? (
        <div className="w-full flex items-center justify-center text-[11px] text-[#6B7280] bg-[#0D1117] border border-[#1F2937]" style={{ height }}>
          No chart data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={merged} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
            <CartesianGrid stroke="#1F2937" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              interval="preserveStartEnd"
              minTickGap={50}
            />
            <YAxis
              yAxisId="spx"
              orientation="left"
              domain={['auto', 'auto']}
              axisLine={false}
              tickLine={false}
              tick={{ fill: SPX_COLOR, fontSize: 10, fontFamily: 'JetBrains Mono' }}
              tickFormatter={(v) => Number(v).toFixed(0)}
              width={55}
            />
            <YAxis
              yAxisId="vix"
              orientation="right"
              domain={['auto', 'auto']}
              axisLine={false}
              tickLine={false}
              tick={{ fill: VIX_COLOR, fontSize: 10, fontFamily: 'JetBrains Mono' }}
              tickFormatter={(v) => Number(v).toFixed(1)}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1A2035',
                border: '1px solid #1F2937',
                borderRadius: '2px',
                fontSize: '12px',
                fontFamily: 'JetBrains Mono',
                color: '#E5E7EB',
              }}
              labelStyle={{ color: '#9CA3AF' }}
              formatter={(value, name) => {
                const label = name === 'spx' ? 'S&P 500' : 'VIX';
                if (value == null) return ['—', label];
                return [(value as number).toFixed(2), label];
              }}
            />
            <Legend
              wrapperStyle={{ display: 'none' }}
            />
            <Line
              yAxisId="spx"
              type="monotone"
              dataKey="spx"
              stroke={SPX_COLOR}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: SPX_COLOR }}
              connectNulls
              isAnimationActive={false}
            />
            <Line
              yAxisId="vix"
              type="monotone"
              dataKey="vix"
              stroke={VIX_COLOR}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: VIX_COLOR }}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
